'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { CheckCircle, Circle, Clock, XCircle, Loader2, AlertCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface ValidationEvent {
  id: string
  type: string
  source?: string
  resourceId?: string
  timestamp: string
  data?: {
    message?: string
    status?: string
    level?: string
    step?: string
    metadata?: unknown
    [key: string]: unknown
  }
}

interface ValidationProgressProps {
  validationId: string
  validationStatus?: string
  selectedInvariant?: ValidationProgressInvariant | null
  embedded?: boolean
}

type StepMetadata = Record<string, unknown>

type ValidationProgressInvariant = {
  id: string
  name: string
  status: string
  message?: string
  description?: string
  type?: string
  details?: Record<string, unknown>
}

type NestedExecutionResult = {
  name: string
  status?: string
  type?: string
  message?: string
  details?: Record<string, unknown>
}

const STEP_INFO = {
  'validation.queued': {
    label: 'Queued',
    icon: Circle,
    description: 'Validation request queued for processing',
  },
  'validation.started': {
    label: 'Validation Started',
    icon: Loader2,
    description: 'Validation is running',
  },
  'validation.result': {
    label: 'Validation Complete',
    icon: CheckCircle,
    description: 'Validation finished',
  },
  'validation.logs': {
    label: 'Progress Update',
    icon: Loader2,
    description: 'Validation progress',
  },
}

const formatLabel = (value?: string) => {
  if (!value) {
    return 'Unknown'
  }
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

const isPrimitive = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null

const getStringField = (record: Record<string, unknown> | null, keys: string[]) => {
  if (!record) {
    return ''
  }

  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) {
      return value
    }
  }

  return ''
}

const getStringList = (value: unknown) => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((entry) => (typeof entry === 'string' || typeof entry === 'number' ? String(entry) : ''))
    .map((entry) => entry.trim())
    .filter(Boolean)
}

const formatValue = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toFixed(2)
  }
  return String(value)
}

const EXECUTION_DETAIL_EXCLUDED_KEYS = new Set([
  'findings',
  'violations',
  'paths',
  'flows',
  'unreachable',
  'traces',
  'failed_traces',
  'session_list',
  'sessions',
  'failure_analysis',
  'mtu_groups',
  'sample_flows',
  'impacted_paths',
  'by_disposition',
  'counts',
  'results',
  'details',
])

const getExecutionMetricRows = (details: Record<string, unknown> | undefined) => {
  if (!details) {
    return []
  }

  const metrics: Array<{ label: string; value: string }> = []
  const pushMetric = (key: string, value: unknown) => {
    if (!isPrimitive(value)) {
      return
    }
    metrics.push({ label: formatLabel(key), value: formatValue(value) })
  }

  const summary = asRecord(details.summary)
  if (summary) {
    Object.entries(summary).forEach(([key, value]) => pushMetric(key, value))
  }

  const counts = asRecord(details.counts)
  if (counts) {
    Object.entries(counts).forEach(([key, value]) => pushMetric(key, value))
  }

  Object.entries(details).forEach(([key, value]) => {
    if (key === 'summary' || key === 'counts' || EXECUTION_DETAIL_EXCLUDED_KEYS.has(key)) {
      return
    }
    pushMetric(key, value)
  })

  return metrics
}

const getRecordList = (value: unknown): Array<Record<string, unknown>> => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter((entry): entry is Record<string, unknown> =>
    Boolean(entry && typeof entry === 'object' && !Array.isArray(entry)),
  )
}

const getDisplayFields = (record: Record<string, unknown>, preferredKeys: string[]) => {
  const preferred = preferredKeys
    .filter((key) => isPrimitive(record[key]))
    .map((key) => ({ label: formatLabel(key), value: formatValue(record[key]) }))

  if (preferred.length > 0) {
    return preferred
  }

  return Object.entries(record)
    .filter(([, value]) => isPrimitive(value))
    .slice(0, 4)
    .map(([key, value]) => ({ label: formatLabel(key), value: formatValue(value) }))
}

const getDispositionBreakdown = (value: unknown) => {
  const record = asRecord(value)
  if (!record) {
    return []
  }

  return Object.entries(record)
    .filter(([, count]) => isPrimitive(count))
    .map(([label, count]) => ({ label: formatLabel(label), value: formatValue(count) }))
}

const getNestedExecutionResults = (details: Record<string, unknown> | undefined): NestedExecutionResult[] => {
  return getRecordList(details?.results).map((result, index) => ({
    name: typeof result.name === 'string' && result.name.trim() ? result.name : `Result ${index + 1}`,
    status: typeof result.status === 'string' ? result.status : undefined,
    type: typeof result.type === 'string' ? result.type : undefined,
    message: typeof result.message === 'string' ? result.message : undefined,
    details: asRecord(result.details) ?? undefined,
  }))
}

const getFailureAnalysisSummary = (entry: Record<string, unknown>) => {
  const details = asRecord(entry.details)
  if (typeof details?.findings === 'string' && details.findings.trim()) {
    return details.findings
  }
  const violations = getRecordList(entry.violations)
  if (violations.length > 0) {
    const first = violations[0]
    if (typeof first.details === 'string' && first.details.trim()) {
      return first.details
    }
    if (typeof first.message === 'string' && first.message.trim()) {
      return first.message
    }
  }
  return ''
}

const getHopLabel = (hop: unknown) => {
  if (typeof hop === 'string' && hop.trim()) {
    return hop
  }

  const hopRecord = asRecord(hop)
  if (!hopRecord) {
    return ''
  }

  const nodeLabel = getStringField(asRecord(hopRecord.node ?? hopRecord.Node), ['name', 'id', 'hostname'])
  if (nodeLabel) {
    return nodeLabel
  }

  return getStringField(hopRecord, ['name', 'id', 'hostname', 'node', 'Node'])
}

const getPathSummary = (entry: Record<string, unknown>) => {
  const hopList = Array.isArray(entry.hops) ? entry.hops.map(getHopLabel).filter(Boolean) : []
  if (hopList.length > 0) {
    return hopList.join(' -> ')
  }

  if (typeof entry.flow === 'string' && entry.flow.trim()) {
    return entry.flow
  }

  const source = typeof entry.source === 'string'
    ? entry.source
    : typeof entry.Flow === 'string'
      ? entry.Flow
      : ''
  const destination = typeof entry.destination === 'string' ? entry.destination : ''
  const disposition = typeof entry.disposition === 'string'
    ? entry.disposition
    : typeof entry.Flow_Disposition === 'string'
      ? entry.Flow_Disposition
      : ''
  const forwardDisposition = typeof entry.forward_disposition === 'string' ? entry.forward_disposition : ''
  const returnDisposition = typeof entry.return_disposition === 'string' ? entry.return_disposition : ''

  if (forwardDisposition || returnDisposition) {
    return [
      source,
      destination ? `to ${destination}` : '',
      forwardDisposition ? `Forward ${forwardDisposition}` : '',
      returnDisposition ? `Return ${returnDisposition}` : '',
    ].filter(Boolean).join(' ')
  }

  return [source, destination ? `to ${destination}` : '', disposition].filter(Boolean).join(' ')
}

const getMultipathHeadline = (details: Record<string, unknown> | undefined) => {
  if (!details) {
    return ''
  }

  const source = typeof details.source === 'string' ? details.source : ''
  const destination = typeof details.destination === 'string' ? details.destination : ''
  if (source && destination) {
    return `${source} -> ${destination}`
  }

  return ''
}

const getMultipathSummary = (details: Record<string, unknown> | undefined) => {
  if (!details) {
    return ''
  }

  const summaryParts: string[] = []
  const pathCount = typeof details.paths_found === 'number' ? details.paths_found : undefined
  const minPaths = typeof details.min_paths_required === 'number' ? details.min_paths_required : undefined
  const maxPaths = typeof details.max_paths_allowed === 'number' ? details.max_paths_allowed : undefined
  const expectedPaths = typeof details.expected_paths === 'number' ? details.expected_paths : undefined
  const protocols = getStringList(details.protocols)
  const ports = getStringList(details.ports)

  if (typeof pathCount === 'number') {
    summaryParts.push(`${pathCount} path${pathCount === 1 ? '' : 's'} available`)
  }
  if (typeof minPaths === 'number') {
    summaryParts.push(`minimum ${minPaths}`)
  }
  if (typeof expectedPaths === 'number') {
    summaryParts.push(`expected ${expectedPaths}`)
  }
  if (typeof maxPaths === 'number') {
    summaryParts.push(`maximum ${maxPaths}`)
  }
  if (protocols.length > 0) {
    summaryParts.push(`protocols ${protocols.join(', ')}`)
  }
  if (ports.length > 0) {
    summaryParts.push(`ports ${ports.join(', ')}`)
  }

  return summaryParts.join(' · ')
}

const getExecutionPaths = (details: Record<string, unknown> | undefined) =>
  getRecordList(
    details?.paths ??
    details?.sample_flows ??
    details?.impacted_paths ??
    details?.flows ??
    details?.unreachable ??
    details?.failed_traces ??
    details?.traces,
  )

const eventMatchesSelectedInvariant = (
  metadata: StepMetadata | undefined,
  selectedInvariant: ValidationProgressInvariant | null | undefined,
) => {
  if (!selectedInvariant) {
    return true
  }

  const invariantID = typeof metadata?.invariant_id === 'string' ? metadata.invariant_id : undefined
  const invariantName = typeof metadata?.invariant_name === 'string'
    ? metadata.invariant_name
    : typeof metadata?.invariant === 'string'
      ? metadata.invariant
      : undefined

  return invariantID === selectedInvariant.id || invariantName === selectedInvariant.name
}

export function ValidationProgress({
  validationId,
  validationStatus,
  selectedInvariant,
  embedded = false,
}: ValidationProgressProps) {
  const [events, setEvents] = useState<ValidationEvent[]>([])
  const [status, setStatus] = useState<string>('')
  const [connectionState, setConnectionState] = useState<'live' | 'reconnecting' | 'disconnected'>('disconnected')
  const [error, setError] = useState<string | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const processedEventIds = useRef<Set<string>>(new Set())
  const [isTerminalState, setIsTerminalState] = useState(false)

  useEffect(() => {
    if (!validationId) return

    // Check if validation is already in a terminal state from props
    const terminalStates = ['success', 'failure', 'error', 'cancelled', 'completed', 'failed']
    const isAlreadyTerminal = validationStatus && terminalStates.includes(validationStatus.toLowerCase())

    if (isAlreadyTerminal) {
      setIsTerminalState(true)
      setStatus(validationStatus.toLowerCase())
      return
    }

    // Don't reconnect if validation is in a terminal state
    if (isTerminalState) {
      return
    }

    let cancelled = false
    const apiUrl = window.location.origin
    const cleanupConnection = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }

    const scheduleReconnect = () => {
      if (cancelled || isTerminalState) {
        return
      }
      reconnectAttemptsRef.current += 1
      const delayMs = Math.min(1000 * reconnectAttemptsRef.current, 5000)
      setConnectionState('reconnecting')
      setError('Reconnecting to live updates...')
      reconnectTimerRef.current = setTimeout(() => {
        connect()
      }, delayMs)
    }

    const connect = () => {
      if (cancelled || isTerminalState) {
        return
      }

      const token = localStorage.getItem('access_token')
      if (!token) {
        setConnectionState('disconnected')
        setError('Authentication required for real-time updates')
        return
      }

      cleanupConnection()
      const url = `${apiUrl}/api/v1/validations/${validationId}/progress?token=${encodeURIComponent(token)}`
      const eventSource = new EventSource(url)
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        reconnectAttemptsRef.current = 0
        setConnectionState('live')
        setError(null)
      }

      eventSource.onerror = () => {
        cleanupConnection()
        scheduleReconnect()
      }

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'event' && data.event) {
            const incoming = data.event as ValidationEvent
            const eventId = incoming.id

            if (eventId && processedEventIds.current.has(eventId)) {
              return
            }
            if (eventId) {
              processedEventIds.current.add(eventId)
            }

            setEvents(prev => {
              const newEvents = [...prev, incoming]
              return newEvents.sort((a, b) =>
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
              )
            })

            setConnectionState('live')
            setError(null)

            if (incoming.type === 'validation.started') {
              setStatus('running')
            }

            if (incoming.type === 'validation.result') {
              const rawStatus = (incoming.data?.status || '').toString().toLowerCase()
              if (['success', 'completed', 'passed'].includes(rawStatus)) {
                setStatus('success')
              } else {
                setStatus('failure')
              }
              setIsTerminalState(true)
              setConnectionState('disconnected')
              cleanupConnection()
            }
          } else if (data.type === 'error') {
            setError(data.message || 'An error occurred')
          }
        } catch (parseError) {
          console.error('Error parsing SSE data:', parseError)
        }
      }
    }

    connect()

    // Cleanup on unmount
    return () => {
      cancelled = true
      cleanupConnection()
    }
  }, [validationId, validationStatus, isTerminalState])

  // Combine validation events for display
  const allSteps = events.map(e => {
    const isLog = e.type === 'validation.logs'
    const step = isLog ? (e.data?.step as string) || e.type : e.type || 'unknown'
    return {
      key: `event-${e.id}`,
      step,
      status: e.data?.status || 'completed',
      message: e.data?.message,
      level: e.data?.level,
      metadata: (e.data?.metadata ?? e.data) as StepMetadata | undefined,
      timestamp: e.timestamp,
      isLog,
    }
  }).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

  // Remove duplicates based on step name, but keep all log entries
  const uniqueSteps = allSteps.reduce((acc, curr) => {
    if (curr.isLog) {
      return [...acc, curr]
    }

    const existing = acc.find(s => s.step === curr.step)
    if (!existing || new Date(curr.timestamp) > new Date(existing.timestamp)) {
      return [...acc.filter(s => s.step !== curr.step), curr]
    }
    return acc
  }, [] as typeof allSteps)

  const stepsToShow = useMemo(() => {
    if (!selectedInvariant) {
      return uniqueSteps
    }

    return uniqueSteps.filter((step) => eventMatchesSelectedInvariant(step.metadata, selectedInvariant))
  }, [selectedInvariant, uniqueSteps])

  const nestedExecutionResults = useMemo(
    () => getNestedExecutionResults(selectedInvariant?.details),
    [selectedInvariant?.details],
  )
  const singleNestedResult = nestedExecutionResults.length === 1 ? nestedExecutionResults[0] : null
  const executionDetailMetrics = useMemo(
    () => getExecutionMetricRows(selectedInvariant?.details),
    [selectedInvariant?.details],
  )
  const nestedExecutionDetailMetrics = useMemo(
    () => getExecutionMetricRows(singleNestedResult?.details),
    [singleNestedResult?.details],
  )
  const executionSessions = useMemo(
    () => {
      const direct = getRecordList(selectedInvariant?.details?.session_list ?? selectedInvariant?.details?.sessions)
      if (direct.length > 0) {
        return direct
      }
      return getRecordList(singleNestedResult?.details?.session_list ?? singleNestedResult?.details?.sessions)
    },
    [selectedInvariant?.details, singleNestedResult?.details],
  )
  const executionFailures = useMemo(
    () => {
      const direct = getRecordList(selectedInvariant?.details?.failure_analysis)
      if (direct.length > 0) {
        return direct
      }
      return getRecordList(singleNestedResult?.details?.failure_analysis)
    },
    [selectedInvariant?.details, singleNestedResult?.details],
  )
  const executionDispositionBreakdown = useMemo(
    () => {
      const direct = getDispositionBreakdown(selectedInvariant?.details?.by_disposition)
      if (direct.length > 0) {
        return direct
      }
      return getDispositionBreakdown(singleNestedResult?.details?.by_disposition)
    },
    [selectedInvariant?.details, singleNestedResult?.details],
  )
  const executionPaths = useMemo(
    () => {
      const direct = getExecutionPaths(selectedInvariant?.details)
      if (direct.length > 0) {
        return direct
      }
      return getExecutionPaths(singleNestedResult?.details)
    },
    [selectedInvariant?.details, singleNestedResult?.details],
  )
  const executionMtuGroups = useMemo(
    () => {
      const direct = getRecordList(selectedInvariant?.details?.mtu_groups)
      if (direct.length > 0) {
        return direct
      }
      return getRecordList(singleNestedResult?.details?.mtu_groups)
    },
    [selectedInvariant?.details, singleNestedResult?.details],
  )
  const hasStructuredExecutionDetails = (
    executionDetailMetrics.length > 0 ||
    nestedExecutionDetailMetrics.length > 0 ||
    executionDispositionBreakdown.length > 0 ||
    executionSessions.length > 0 ||
    executionFailures.length > 0 ||
    executionPaths.length > 0 ||
    executionMtuGroups.length > 0 ||
    nestedExecutionResults.length > 0
  )

  const getStepIcon = (
    step: string,
    stepStatus: string,
    level?: string,
    metadata?: StepMetadata,
    isLog?: boolean
  ) => {
    const info = STEP_INFO[step as keyof typeof STEP_INFO]
    const Icon = info?.icon || Circle

    // Handle completion events - check overall_status from metadata
    if (step === 'validation.result') {
      const overallStatus = metadata?.overall_status
      if (overallStatus === 'passed') return <CheckCircle className="w-5 h-5" style={{ color: 'rgb(34 197 94)' }} />
      if (overallStatus === 'failed') return <XCircle className="w-5 h-5" style={{ color: 'rgb(239 68 68)' }} />
      if (overallStatus === 'warning') return <AlertCircle className="w-5 h-5" style={{ color: 'rgb(234 179 8)' }} />
      if (['failed', 'failure', 'error'].includes(stepStatus)) {
        return <XCircle className="w-5 h-5" style={{ color: 'rgb(239 68 68)' }} />
      }
      // Default to green if completed without overall_status
      return <CheckCircle className="w-5 h-5" style={{ color: 'rgb(34 197 94)' }} />
    }

    // Handle validation.logs events based on invariant_status if available, otherwise level
    if (isLog) {
      // Check for invariant_status in metadata first (more specific)
      const invariantStatus = metadata?.invariant_status
      if (invariantStatus === 'failed' || invariantStatus === 'error') {
        return <XCircle className="w-5 h-5" style={{ color: 'rgb(239 68 68)' }} />
      }
      if (invariantStatus === 'warning') {
        return <AlertCircle className="w-5 h-5" style={{ color: 'rgb(234 179 8)' }} />
      }
      if (invariantStatus === 'passed') {
        return <CheckCircle className="w-5 h-5" style={{ color: 'rgb(34 197 94)' }} />
      }

      // Fallback to level for non-invariant log events
      if (level === 'ERROR') return <XCircle className="w-5 h-5" style={{ color: 'rgb(239 68 68)' }} />
      if (level === 'WARNING') return <AlertCircle className="w-5 h-5" style={{ color: 'rgb(234 179 8)' }} />
      return <Circle className="w-5 h-5" style={{ color: 'rgb(59 130 246)' }} />
    }

    // Handle other events by status
    if (['failed', 'failure', 'error'].includes(stepStatus)) return <XCircle className="w-5 h-5" style={{ color: 'rgb(239 68 68)' }} />
    if (['completed', 'success', 'passed'].includes(stepStatus)) return <CheckCircle className="w-5 h-5" style={{ color: 'rgb(34 197 94)' }} />
    if (stepStatus === 'in_progress' || stepStatus === 'running') {
      if (info?.icon === Loader2) {
        return <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'rgb(59 130 246)' }} />
      }
      return <Clock className="w-5 h-5" style={{ color: 'rgb(59 130 246)' }} />
    }

    return <Icon className="w-5 h-5 text-muted-foreground" />
  }

  const getStepLabel = (step: string) => {
    const info = STEP_INFO[step as keyof typeof STEP_INFO]
    return info?.label || step.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const getStepDescription = (step: string, message?: string) => {
    if (message) return message
    const info = STEP_INFO[step as keyof typeof STEP_INFO]
    return info?.description || ''
  }

  if (!validationId) {
    if (embedded) {
      return (
        <div className="rounded-lg border border-border/30 bg-muted/20 px-4 py-6 text-center text-muted-foreground">
          Validation ID is required
        </div>
      )
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle>Validation Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            Validation ID is required
          </div>
        </CardContent>
      </Card>
    )
  }

  const liveStatus = connectionState === 'live' ? (
    <div className="flex items-center gap-1">
      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
      <span className="text-sm text-muted-foreground">Live</span>
    </div>
  ) : connectionState === 'reconnecting' ? (
    <div className="flex items-center gap-1">
      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
      <span className="text-sm text-muted-foreground">Reconnecting</span>
    </div>
  ) : null

  const content = (
    <div className="space-y-4">
      {selectedInvariant && !embedded ? (
        <div className="rounded-lg border border-border/30 bg-muted/20 px-4 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Selected invariant</p>
          <div className="mt-2 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="break-words text-sm font-medium text-foreground">{selectedInvariant.name}</p>
              {selectedInvariant.description ? (
                <p className="mt-1 break-words text-sm leading-6 text-muted-foreground">{selectedInvariant.description}</p>
              ) : null}
            </div>
            {selectedInvariant.type ? (
              <Badge variant="outline" className="shrink-0 border-border/60 bg-background text-muted-foreground">
                {formatLabel(selectedInvariant.type)}
              </Badge>
            ) : null}
          </div>
        </div>
      ) : null}

      {selectedInvariant && hasStructuredExecutionDetails ? (
        <div className="space-y-4 rounded-lg border border-border/30 bg-background px-4 py-4">
          <p className="text-sm font-medium text-foreground">Execution details</p>

          {executionDetailMetrics.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {executionDetailMetrics.map((metric) => (
                <div key={`${metric.label}-${metric.value}`} className="rounded-lg border border-border/30 bg-muted/20 px-3 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{metric.label}</p>
                  <p className="mt-2 text-sm font-medium text-foreground">{metric.value}</p>
                </div>
              ))}
            </div>
          ) : null}

          {nestedExecutionDetailMetrics.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">
                {singleNestedResult ? `${singleNestedResult.name} details` : 'Target details'}
              </p>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {nestedExecutionDetailMetrics.map((metric) => (
                  <div key={`nested-${metric.label}-${metric.value}`} className="rounded-lg border border-border/30 bg-muted/20 px-3 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{metric.label}</p>
                    <p className="mt-2 text-sm font-medium text-foreground">{metric.value}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {executionDispositionBreakdown.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Disposition breakdown</p>
              <div className="flex flex-wrap gap-2">
                {executionDispositionBreakdown.map((entry) => (
                  <Badge
                    key={`disposition-${entry.label}`}
                    variant="outline"
                    className="border-border/60 bg-muted/20 text-foreground"
                  >
                    {entry.label}: {entry.value}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          {executionFailures.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Failure scenarios</p>
              {executionFailures.slice(0, 4).map((entry, index) => (
                <div key={`failure-${index}`} className="rounded-lg border border-border/30 bg-muted/20 px-3 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-words text-sm font-medium text-foreground">
                        {typeof entry.failure === 'string' ? entry.failure : `Scenario ${index + 1}`}
                      </p>
                      {getFailureAnalysisSummary(entry) ? (
                        <p className="mt-1 break-words text-xs leading-5 text-muted-foreground">
                          {getFailureAnalysisSummary(entry)}
                        </p>
                      ) : null}
                    </div>
                    {typeof entry.status === 'string' ? (
                      <Badge variant="outline" className="shrink-0 border-border/60 bg-background text-muted-foreground">
                        {formatLabel(entry.status)}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {executionSessions.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Session checks</p>
              {executionSessions.slice(0, 5).map((session, index) => (
                <div key={`session-${index}`} className="rounded-lg border border-border/30 bg-muted/20 px-3 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-words text-sm font-medium text-foreground">
                        {[session.node, session.node1, session.local_ip].filter(Boolean).map((value) => String(value)).join(' / ') || `Session ${index + 1}`}
                      </p>
                      <p className="mt-1 break-words text-xs leading-5 text-muted-foreground">
                        {getDisplayFields(session, ['remote_node', 'node2', 'peer', 'remote_ip', 'local_as', 'remote_as'])
                          .map((field) => `${field.label}: ${field.value}`)
                          .join(' · ')}
                      </p>
                    </div>
                    {typeof session.status === 'string' ? (
                      <Badge variant="outline" className="shrink-0 border-border/60 bg-background text-muted-foreground">
                        {formatLabel(session.status)}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {executionPaths.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Path and flow samples</p>
              {executionPaths.slice(0, 5).map((path, index) => (
                <div key={`path-${index}`} className="rounded-lg border border-border/30 bg-muted/20 px-3 py-3">
                  <p className="break-words text-sm font-medium text-foreground">
                    {getPathSummary(path) || `Path ${index + 1}`}
                  </p>
                  <p className="mt-1 break-words text-xs leading-5 text-muted-foreground">
                    {getDisplayFields(path, [
                      'source',
                      'destination',
                      'flow',
                      'protocol',
                      'port',
                      'disposition',
                      'Flow_Disposition',
                      'forward_disposition',
                      'return_disposition',
                      'trace_count',
                      'is_success',
                    ])
                      .map((field) => `${field.label}: ${field.value}`)
                      .join(' · ')}
                  </p>
                </div>
              ))}
            </div>
          ) : null}

          {nestedExecutionResults.length > 1 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Target results</p>
              {nestedExecutionResults.slice(0, 5).map((result, index) => {
                const resultMetrics = getExecutionMetricRows(result.details)
                const resultDispositionBreakdown = getDispositionBreakdown(result.details?.by_disposition)
                const resultPaths = getExecutionPaths(result.details)
                const multipathHeadline = result.type === 'multipath' ? getMultipathHeadline(result.details) : ''
                const multipathSummary = result.type === 'multipath' ? getMultipathSummary(result.details) : ''

                return (
                  <div key={`target-result-${index}`} className="rounded-lg border border-border/30 bg-muted/20 px-3 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="break-words text-sm font-medium text-foreground">{multipathHeadline || result.name}</p>
                        {multipathSummary ? (
                          <p className="mt-1 break-words text-xs leading-5 text-muted-foreground">{multipathSummary}</p>
                        ) : null}
                        {!multipathSummary && result.message ? (
                          <p className="mt-1 break-words text-xs leading-5 text-muted-foreground">{result.message}</p>
                        ) : null}
                      </div>
                      {result.status ? (
                        <Badge variant="outline" className="shrink-0 border-border/60 bg-background text-muted-foreground">
                          {formatLabel(result.status)}
                        </Badge>
                        ) : null}
                      </div>

                    {result.type !== 'multipath' && resultMetrics.length > 0 ? (
                      <p className="mt-2 break-words text-xs leading-5 text-muted-foreground">
                        {resultMetrics.slice(0, 4).map((metric) => `${metric.label}: ${metric.value}`).join(' · ')}
                      </p>
                    ) : null}

                    {resultDispositionBreakdown.length > 0 ? (
                      <p className="mt-2 break-words text-xs leading-5 text-muted-foreground">
                        {resultDispositionBreakdown.map((entry) => `${entry.label}: ${entry.value}`).join(' · ')}
                      </p>
                    ) : null}

                    {resultPaths.length > 0 ? (
                      <div className="mt-2 space-y-1">
                        {resultPaths.map((path, pathIndex) => (
                          <p key={`target-result-${index}-path-${pathIndex}`} className="break-words text-xs leading-5 text-muted-foreground">
                            {getPathSummary(path) || `Path ${pathIndex + 1}`}
                          </p>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          ) : null}

          {executionMtuGroups.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">MTU distribution</p>
              {executionMtuGroups.slice(0, 4).map((group, index) => (
                <div key={`mtu-${index}`} className="rounded-lg border border-border/30 bg-muted/20 px-3 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-medium text-foreground">
                      MTU {typeof group.mtu_value === 'number' || typeof group.mtu_value === 'string' ? String(group.mtu_value) : 'Unknown'}
                    </p>
                    {isPrimitive(group.interface_count) ? (
                      <Badge variant="outline" className="shrink-0 border-border/60 bg-background text-muted-foreground">
                        {formatValue(group.interface_count)} interfaces
                      </Badge>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {stepsToShow.length === 0 && !hasStructuredExecutionDetails ? (
        <div className="text-center py-8">
          {selectedInvariant ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">No per-invariant execution updates were recorded</p>
              <p className="text-sm text-muted-foreground">
                {selectedInvariant.message || 'Use the structured execution details on this pane and the supporting sections on this page.'}
              </p>
            </div>
          ) : (() => {
            const completedStates = ['success', 'failure', 'error', 'cancelled', 'completed']
            if (validationStatus && completedStates.includes(validationStatus.toLowerCase())) {
              if (validationStatus.toLowerCase() === 'success') {
                return (
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <span>Validation completed successfully</span>
                  </div>
                )
              } else if (['failure', 'error'].includes(validationStatus.toLowerCase())) {
                return (
                  <div className="flex items-center justify-center gap-2 text-red-600">
                    <XCircle className="w-5 h-5" />
                    <span>Validation failed</span>
                  </div>
                )
              } else {
                return (
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <AlertCircle className="w-5 h-5" />
                    <span>Validation {validationStatus}</span>
                  </div>
                )
              }
            }
            return (
              <div className="text-muted-foreground">
                Waiting for validation to start...
              </div>
            )
          })()}
        </div>
      ) : null}

      {stepsToShow.length > 0 ? (
        (() => {
          const completedStates = ['success', 'failure', 'error', 'cancelled', 'completed']
          const isCompleted = validationStatus && completedStates.includes(validationStatus.toLowerCase())

          if (isCompleted) {
            const lastStep = stepsToShow[stepsToShow.length - 1]
            if (lastStep && (
              lastStep.step.includes('completed') ||
              lastStep.step.includes('success') ||
              lastStep.step.includes('failed') ||
              (lastStep.message && (
                lastStep.message.toLowerCase().includes('completed successfully') ||
                lastStep.message.toLowerCase().includes('validation failed')
              ))
            )) {
              // Keep full progress history visible even when the last item is a completion event.
            }
          }

          return (
            <div className={cn('space-y-4', selectedInvariant && 'border-t border-border/30 pt-4')}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-foreground">
                  {selectedInvariant ? 'Selected invariant updates' : 'Execution timeline'}
                </p>
                <span className="text-xs text-muted-foreground">{stepsToShow.length} events</span>
              </div>
              {stepsToShow.map((step, index) => (
                <div key={step.key} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    {getStepIcon(step.step, step.status, step.level, step.metadata, step.isLog)}
                    {index < stepsToShow.length - 1 && (
                      <div className="mt-2 h-12 w-0.5 bg-border" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{getStepLabel(step.step)}</div>
                    <div className="text-sm text-muted-foreground">
                      {getStepDescription(step.step, step.message)}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {new Date(step.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        })()
      ) : null}

      {!selectedInvariant && (status === 'success' || status === 'completed') && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Validation completed successfully</span>
          </div>
        </div>
      )}

      {!selectedInvariant && status === 'failure' && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700">
            <XCircle className="w-5 h-5" />
            <span className="font-medium">Validation failed</span>
          </div>
        </div>
      )}
    </div>
  )

  if (embedded) {
    return (
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3 border-b border-border/20 pb-3">
          <p className="text-sm font-medium text-foreground">{selectedInvariant ? 'Invariant progress' : 'Validation Progress'}</p>
          {liveStatus}
        </div>
        {error ? (
          <div className="flex items-center gap-2 text-sm text-yellow-600">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        ) : null}
        {content}
      </section>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{selectedInvariant ? 'Invariant progress' : 'Validation Progress'}</CardTitle>
          {liveStatus}
        </div>
        {error && (
          <div className="flex items-center gap-2 mt-2 text-sm text-yellow-600">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}
